// EMPLOYEE PRODUCTIVITY REPORT
// Individual employee performance tracking and analysis.
// Phase 7B: Employee & Export

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {getEmployeeProductivityReport} from '../../../../api/ReportsAPI'
import {useAuth} from '../../../modules/auth'
import ExportButton from './ExportButton'

interface EmployeeProductivityProps {
  startDate: string
  endDate: string
  managerId: string
}

interface EmployeeMetrics {
  totalHours: number
  billableHours: number
  billableRate: number
  overtimeHours: number
  approvalRate: number
  rejectionCount: number
  uniqueProjects: number
  averageHoursPerDay: number
}

interface EmployeeInfo {
  id: string
  employeeCode: string
  name: string
  email: string
}

interface DailyBreakdown {
  date: string
  billableHours: number
  actualHours: number
  entriesCount: number
}

interface ProjectBreakdown {
  projectCode: string
  projectName: string
  hours: number
  percentage: number
}

interface ToolBreakdown {
  toolName: string
  hours: number
  percentage: number
}

interface ProductivityData {
  employee: EmployeeInfo
  metrics: EmployeeMetrics
  dailyBreakdown: DailyBreakdown[]
  projectBreakdown: ProjectBreakdown[]
  toolBreakdown: ToolBreakdown[]
}

const EmployeeProductivity: React.FC<EmployeeProductivityProps> = ({
  startDate,
  endDate,
  managerId,
}) => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ProductivityData | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [teamMembers, setTeamMembers] = useState<Array<{id: string; name: string}>>([])

  // Fetch team members for employee selector
  useEffect(() => {
    fetchTeamMembers()
  }, [managerId])

  // Fetch report when user is selected
  useEffect(() => {
    if (selectedUserId && startDate && endDate) {
      fetchReport()
    }
  }, [selectedUserId, startDate, endDate])

  const fetchTeamMembers = async () => {
    try {
      // Use the team utilization API to get team members
      // We'll make a lightweight call to get team member list
      const response = await fetch(
        `http://localhost:8000/api/v1/timesheet/reports/team-utilization?managerId=${managerId}&startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
      const result = await response.json()
      if (result.statusCode === 200 && result.data?.byEmployee) {
        const members = result.data.byEmployee.map((emp: any) => ({
          id: emp.userId,
          name: emp.name,
        }))
        setTeamMembers(members)
        // Auto-select current user if they're in the team
        if (currentUser?.id && members.some((m: any) => m.id === currentUser.id)) {
          setSelectedUserId(currentUser.id)
        } else if (members.length > 0) {
          setSelectedUserId(members[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error)
    }
  }

  const fetchReport = async () => {
    if (!selectedUserId) return

    setLoading(true)
    try {
      const response = await getEmployeeProductivityReport({
        userId: selectedUserId,
        startDate,
        endDate,
      })

      if (response.data?.statusCode === 200) {
        setData(response.data.data)
      } else {
        toast.error('Failed to load employee productivity report')
      }
    } catch (error: any) {
      console.error('Error fetching employee productivity:', error)
      toast.error(error?.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  // Render loading state
  if (loading) {
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
        <p className='text-muted mt-3'>Loading employee productivity report...</p>
      </div>
    )
  }

  // Render employee selector if no user selected
  if (!selectedUserId || teamMembers.length === 0) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-user-circle fa-3x text-muted mb-3'></i>
        <p className='text-muted'>Please select an employee to view their productivity report</p>
        {teamMembers.length === 0 && (
          <p className='text-muted'>
            <small>No team members found for the selected date range</small>
          </p>
        )}
      </div>
    )
  }

  // Render empty state
  if (!data) {
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
        <p className='text-muted mt-3'>Loading...</p>
      </div>
    )
  }

  // Utility function to get color based on rate
  const getRateColor = (rate: number): string => {
    if (rate >= 90) return 'success'
    if (rate >= 75) return 'info'
    if (rate >= 60) return 'warning'
    return 'danger'
  }

  return (
    <>
      {/* Employee Selector and Export */}
      <Row className='mb-4'>
        <Col md={5}>
          <label className='form-label fw-bold'>Select Employee</label>
          <select
            className='form-select'
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            {teamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </Col>
        <Col md={5} className='d-flex align-items-end'>
          <div>
            <p className='mb-0'>
              <strong>Employee Code:</strong> {data.employee.employeeCode}
            </p>
            <p className='mb-0 text-muted small'>{data.employee.email}</p>
          </div>
        </Col>
        <Col md={2} className='d-flex align-items-end justify-content-end'>
          <ExportButton
            reportType='employee-productivity'
            filters={{
              userId: selectedUserId,
              startDate,
              endDate,
            }}
          />
        </Col>
      </Row>

      {/* KPI Summary Cards */}
      <Row className='mb-4'>
        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-primary border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Total Hours</p>
                  <h3 className='mb-0'>{data.metrics.totalHours.toFixed(1)}h</h3>
                </div>
                <i className='fas fa-clock fa-2x text-primary opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-success border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Billable Hours</p>
                  <h3 className='mb-0'>{data.metrics.billableHours.toFixed(1)}h</h3>
                  <small className='text-success'>{data.metrics.billableRate.toFixed(1)}% rate</small>
                </div>
                <i className='fas fa-dollar-sign fa-2x text-success opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-info border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Approval Rate</p>
                  <h3 className='mb-0'>{data.metrics.approvalRate.toFixed(1)}%</h3>
                  <small className='text-muted'>{data.metrics.rejectionCount} rejected</small>
                </div>
                <i className='fas fa-check-circle fa-2x text-info opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-warning border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Avg Hours/Day</p>
                  <h3 className='mb-0'>{data.metrics.averageHoursPerDay.toFixed(1)}h</h3>
                  <small className='text-muted'>{data.metrics.overtimeHours.toFixed(1)}h OT</small>
                </div>
                <i className='fas fa-chart-line fa-2x text-warning opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Additional Metrics Cards */}
      <Row className='mb-4'>
        <Col md={6}>
          <Card className='shadow-sm h-100'>
            <Card.Body>
              <h5 className='mb-3'>
                <i className='fas fa-project-diagram me-2 text-primary'></i>
                Projects Worked On
              </h5>
              <h2 className='mb-0'>{data.metrics.uniqueProjects}</h2>
              <small className='text-muted'>Different projects in this period</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className='shadow-sm h-100'>
            <Card.Body>
              <h5 className='mb-3'>
                <i className='fas fa-clock me-2 text-warning'></i>
                Overtime Summary
              </h5>
              <h2 className='mb-0'>{data.metrics.overtimeHours.toFixed(1)}h</h2>
              <small className='text-muted'>
                {((data.metrics.overtimeHours / data.metrics.totalHours) * 100).toFixed(1)}% of
                total hours
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Daily Breakdown Chart */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-chart-bar me-2'></i>
                Daily Hours Breakdown
              </h5>
            </Card.Header>
            <Card.Body>
              {data.dailyBreakdown.length > 0 ? (
                <div style={{overflowX: 'auto'}}>
                  <div style={{minWidth: '600px'}}>
                    <div className='d-flex align-items-end' style={{height: '250px', gap: '4px'}}>
                      {data.dailyBreakdown.map((day) => {
                        const maxHours = Math.max(
                          ...data.dailyBreakdown.map((d) => d.actualHours)
                        )
                        const billableHeight = (day.billableHours / maxHours) * 200
                        const actualHeight = (day.actualHours / maxHours) * 200

                        return (
                          <div
                            key={day.date}
                            className='flex-fill text-center'
                            style={{position: 'relative'}}
                          >
                            <div
                              className='d-flex justify-content-center align-items-end'
                              style={{height: '200px', gap: '2px'}}
                            >
                              <div
                                className='bg-success rounded'
                                style={{
                                  width: '40%',
                                  height: `${billableHeight}px`,
                                  cursor: 'pointer',
                                }}
                                title={`Billable: ${day.billableHours.toFixed(1)}h`}
                              />
                              <div
                                className='bg-primary rounded'
                                style={{
                                  width: '40%',
                                  height: `${actualHeight}px`,
                                  cursor: 'pointer',
                                }}
                                title={`Actual: ${day.actualHours.toFixed(1)}h`}
                              />
                            </div>
                            <small
                              className='text-muted d-block mt-1'
                              style={{fontSize: '10px', transform: 'rotate(-45deg)'}}
                            >
                              {new Date(day.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </small>
                          </div>
                        )
                      })}
                    </div>
                    <div className='d-flex justify-content-center gap-3 mt-3'>
                      <div>
                        <span className='badge bg-success me-1'>■</span>
                        <small>Billable Hours</small>
                      </div>
                      <div>
                        <span className='badge bg-primary me-1'>■</span>
                        <small>Actual Hours</small>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No daily data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Project and Tool Breakdown */}
      <Row>
        <Col md={6} className='mb-4'>
          <Card className='shadow-sm h-100'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-project-diagram me-2'></i>
                Hours by Project
              </h5>
            </Card.Header>
            <Card.Body>
              {data.projectBreakdown.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-hover table-sm'>
                    <thead className='table-light'>
                      <tr>
                        <th>Project</th>
                        <th className='text-end'>Hours</th>
                        <th className='text-end'>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projectBreakdown.map((project, index) => (
                        <tr key={index}>
                          <td>
                            <div className='fw-bold'>{project.projectCode}</div>
                            <small className='text-muted'>{project.projectName}</small>
                          </td>
                          <td className='text-end'>{project.hours.toFixed(1)}h</td>
                          <td className='text-end'>
                            <span className='badge bg-primary'>{project.percentage.toFixed(1)}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No project data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className='mb-4'>
          <Card className='shadow-sm h-100'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-tools me-2'></i>
                Hours by Tool
              </h5>
            </Card.Header>
            <Card.Body>
              {data.toolBreakdown.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-hover table-sm'>
                    <thead className='table-light'>
                      <tr>
                        <th>Tool</th>
                        <th className='text-end'>Hours</th>
                        <th className='text-end'>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.toolBreakdown.map((tool, index) => (
                        <tr key={index}>
                          <td className='fw-bold'>{tool.toolName}</td>
                          <td className='text-end'>{tool.hours.toFixed(1)}h</td>
                          <td className='text-end'>
                            <span className='badge bg-info'>{tool.percentage.toFixed(1)}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No tool data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default EmployeeProductivity

